import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { QrCode, Download, X } from 'lucide-react';
import { useAuth } from '../lib/auth';

/**
 * Customer-facing QR — encodes `mandispot:khata:<phone>`. The user can show
 * this on their phone at the shop; the merchant scans it and jumps straight
 * to the ledger.
 */
export default function MyKhataQrButton({ variant = 'pill' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState('');
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!open || !user?.phone) return;
    const payload = `mandispot:khata:${user.phone}`;
    QRCode.toDataURL(payload, { width: 320, margin: 2, color: { dark: '#7A4A00', light: '#FFF8EE' } })
      .then(setDataUrl)
      .catch(() => setDataUrl(''));
  }, [open, user]);

  if (!user?.phone) return null;

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `BharatCart-Khata-QR-${user.phone}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const trigger = variant === 'button'
    ? (
      <button data-testid="my-khata-qr-btn-block" onClick={() => setOpen(true)}
        className="btn-primary">
        <QrCode className="w-4 h-4"/>My Khata QR
      </button>
    )
    : (
      <button data-testid="my-khata-qr-btn" onClick={() => setOpen(true)}
        className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">
        <QrCode className="w-3 h-3"/>My Khata QR
      </button>
    );

  return (
    <>
      {trigger}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="max-w-sm w-full bg-white rounded-2xl shadow-2xl p-6 text-center"
            onClick={e => e.stopPropagation()}
            data-testid="my-khata-qr-dialog">
            <div className="flex justify-between items-start">
              <div className="text-left">
                <div className="text-xs uppercase tracking-widest font-bold text-[#D35400]">Show this at the shop</div>
                <div className="font-heading font-black text-xl">Your Khata QR</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-ink/40 hover:text-ink/80"><X className="w-4 h-4"/></button>
            </div>
            {dataUrl
              ? <img src={dataUrl} alt="Khata QR" ref={canvasRef}
                  data-testid="khata-qr-image"
                  className="mx-auto mt-4 rounded-lg border-4 border-[#FFF8EE]"/>
              : <div className="mt-6 text-sm text-ink/60">Generating…</div>
            }
            <div className="mt-2 font-mono text-sm tracking-widest">{user.phone}</div>
            <button data-testid="my-khata-qr-download" onClick={download}
              className="pill !bg-white mt-4 hover:border-[#E67E22] hover:text-[#D35400]">
              <Download className="w-3 h-3"/>Save as image
            </button>
            <div className="mt-4 text-[10px] text-ink/50 uppercase tracking-wider">
              Merchant scans → your ledger opens instantly.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
